import React from "react";
import { makeStyles } from "@material-ui/core/styles";
import Card from "@material-ui/core/Card";
import CardContent from "@material-ui/core/CardContent";
import Typography from "@material-ui/core/Typography";
import Grid from "@material-ui/core/Grid";
import Divider from "@material-ui/core/Divider";
import { useTranslation } from "react-i18next";

const useStyles = makeStyles({
  card: {
    marginBottom: 15,
    border: "1px solid #e0e0e0",
    boxShadow: "0 1px 4px 0 rgba(0, 0, 0, 0.14)",
  },
  title: {
    fontSize: 16,
    fontWeight: 600,
    marginBottom: 12,
  },
  statRow: {
    display: "flex",
    justifyContent: "space-between",
    marginBottom: 8,
  },
  statLabel: {
    fontSize: 14,
    fontWeight: 500,
    color: "#666",
  },
  statValue: {
    fontSize: 14,
    fontWeight: 600,
    color: "#000",
  },
  actionSection: {
    marginTop: 12,
    paddingTop: 12,
    display: "flex",
    justifyContent: "center",
  },
});

/**
 * AssessmentCard Component
 * Card-based display for a single assessment in mobile view
 *
 * @param {string} name - Assessment name
 * @param {string|number} purchased - Number purchased
 * @param {string|number} used - Number used
 * @param {string} available - Available count (may include subscription)
 * @param {string|number} pending - Pending count
 * @param {string|number} completed - Completed count
 * @param {React.ReactNode} action - Checkbox or button component
 */
interface AssessmentCardProps {
  name?: React.ReactNode;
  purchased?: React.ReactNode;
  used?: React.ReactNode;
  available?: React.ReactNode;
  pending?: React.ReactNode;
  completed?: React.ReactNode;
  action?: React.ReactNode;
}

export default function AssessmentCard({
  name,
  purchased,
  used,
  available,
  pending,
  completed,
  action,
}: AssessmentCardProps): React.ReactElement {
  const classes = useStyles();
  const { t } = useTranslation();

  return (
    <Card className={classes.card}>
      <CardContent>
        <Typography className={classes.title}>{name}</Typography>

        <div className={classes.statRow}>
          <Typography className={classes.statLabel}>
            {t("assignments:tableHeaders.purchased")}:
          </Typography>
          <Typography className={classes.statValue}>{purchased}</Typography>
        </div>

        <div className={classes.statRow}>
          <Typography className={classes.statLabel}>
            {t("assignments:tableHeaders.used")}:
          </Typography>
          <Typography className={classes.statValue}>{used}</Typography>
        </div>

        <div className={classes.statRow}>
          <Typography className={classes.statLabel}>
            {t("assignments:tableHeaders.available")}:
          </Typography>
          <Typography className={classes.statValue}>{available}</Typography>
        </div>

        <div className={classes.statRow}>
          <Typography className={classes.statLabel}>
            {t("assignments:tableHeaders.pending")}:
          </Typography>
          <Typography className={classes.statValue}>{pending}</Typography>
        </div>

        <div className={classes.statRow}>
          <Typography className={classes.statLabel}>
            {t("assignments:tableHeaders.completed")}:
          </Typography>
          <Typography className={classes.statValue}>{completed}</Typography>
        </div>

        <Divider />

        <div className={classes.actionSection}>{action}</div>
      </CardContent>
    </Card>
  );
}
